/**
 * Tipos de **Mantenimiento → Sub-activos** (`maintenance_sub_assets`).
 *
 * Un sub-activo es la pieza que se mantiene dentro de un activo (la manguera de
 * un lado, el disco de un servidor). Es el que lleva el **umbral de galonaje**:
 * el estado se calcula con lo leído desde el último cambio.
 *
 * Los usa también el detalle de un activo, que los lista con su consumo.
 */

/** Color con el que el panel pinta el estado de mantenimiento. */
export type ColorEstadoSubActivo = 'danger' | 'warning' | 'success' | 'neutral';

/** Estado calculado frente al umbral. */
export type EstadoSubActivo = 'CRITICO' | 'AVISO' | 'NORMAL' | 'SIN_UMBRAL';

/** Sub-activo con el consumo y el estado ya calculados por la API. */
export interface SubActivo {
  id: number;
  activo_id: number;
  activo: string | null;
  activo_codigo: string | null;
  categoria_id: number | null;
  categoria: string | null;
  codigo: string;
  nombre: string;
  descripcion: string;
  tipo_componente: string;
  /** Lectura acumulada del contador. */
  lectura_actual: number;
  /** Lectura en la que se cambió/limpió por última vez. */
  lectura_cambio: number;
  /** Galones que admite antes de requerir mantenimiento. */
  umbral: number;
  /** Galones consumidos desde el último cambio. */
  usado: number;
  /** Galones que le quedan antes del umbral. */
  restante: number;
  /** Porcentaje consumido (0-100). */
  progreso: number;
  estado_mantenimiento: EstadoSubActivo;
  estado_etiqueta: string;
  estado_color: ColorEstadoSubActivo;
  estado_mensaje: string;
  critico: boolean;
  activo_estado: boolean;
  ultimo_mantenimiento: string | null;
  proximo_mantenimiento: string | null;
  creado: string | null;
  actualizado: string | null;
}

/** Color del estado → variante del componente `Badge`. */
export function varianteEstado(color: ColorEstadoSubActivo): 'danger' | 'warning' | 'success' | 'neutral' {
  return color;
}

/** Filtros del listado. */
export interface SubActivosFiltros {
  page?: number;
  perPage?: number;
  search?: string;
  activoId?: number;
  categoriaId?: number;
  estado?: 'todos' | 'activos' | 'desuso';
}

/** Página de sub-activos. */
export interface SubActivosRespuesta {
  sub_activos: SubActivo[];
  total: number;
  page: number;
  per_page: number;
  paginas: number;
  /** Sub-activos que ya superaron su umbral, dentro de la página devuelta. */
  criticos: number;
  /** Sub-activos que están al 80 % o más del umbral. */
  en_aviso: number;
}

/** Activo disponible para el filtro y el formulario. */
export interface ActivoDeSubActivo {
  id: number;
  codigo: string;
  nombre: string;
  categoria: string;
}

/** Catálogos de la pantalla. */
export interface SubActivosCatalogos {
  activos: ActivoDeSubActivo[];
  categorias: { id: number; nombre: string }[];
  estados: { valor: string; etiqueta: string }[];
}

/** Cuerpo de alta y edición. */
export interface SubActivoPayload {
  activo_id: number;
  codigo: string;
  nombre: string;
  tipo_componente: string;
  descripcion: string;
  /** Galones que admite antes de requerir mantenimiento. */
  umbral: number;
  lectura_actual: number;
  lectura_cambio: number;
  ultimo_mantenimiento: string;
  proximo_mantenimiento: string;
  critico: boolean;
  activo: boolean;
}

/** Respuesta de alta y edición. */
export interface SubActivoGuardado {
  sub_activo: SubActivo;
  mensaje: string;
}

/** Respuesta al marcar un sub-activo como activo o en desuso. */
export interface SubActivoEstadoRespuesta {
  id: number;
  activo: boolean;
  mensaje: string;
}

/** Cuerpo del registro de un mantenimiento (reinicia el contador). */
export interface MantenimientoPayload {
  lectura: number;
  fecha: string;
  proximo: string;
}
